/**
 * Pathway roots are taxonomy rows with type Concentration (CTE) or Course-Subject (TK–12).
 * Assignments/components store the chosen root UUID in concentration / primary_taxonomy_id (dual-written).
 */

/** @param {object} row - assignment or component from API */
export function effectivePrimaryPathwayId(row) {
  if (!row) return null;
  return row.effective_primary_taxonomy_id ?? row.primary_taxonomy_id ?? row.concentration ?? null;
}

/** @param {object} row */
export function effectiveAdditionalPathwayIds(row) {
  if (!row) return [];
  let add = row.effective_additional_taxonomy_ids ?? row.additional_taxonomy_ids ?? row.otherconcentrations;
  if (typeof add === 'string') {
    try {
      add = JSON.parse(add);
    } catch {
      add = [];
    }
  }
  return Array.isArray(add) ? add : [];
}

/**
 * @param {Array<{id:string,title?:string}>} cteConcentrations - from /api/concentrations
 * @param {Array<{id:string,title?:string,type?:string}>} courseSubjects - from /api/taxonomy/course-subjects
 * @returns {Array<{id:string,title:string,pathwayKind:'concentration'|'course_subject'}>}
 */
/**
 * Pathways visible under a scope filter. Optionally pins `alwaysIncludeId` (e.g. primary pathway)
 * so it stays visible when the filter is the other kind.
 */
export function filteredPathwaysForScope(roots = [], scope, { alwaysIncludeId = null } = {}) {
  const list = roots.filter((p) => p.pathwayKind === scope);
  if (!alwaysIncludeId) return list;
  const pin = roots.find((p) => p.id === alwaysIncludeId);
  if (!pin || list.some((p) => p.id === alwaysIncludeId)) return list;
  return [pin, ...list];
}

export function mergePathwayRoots(cteConcentrations = [], courseSubjects = []) {
  const cte = (cteConcentrations || []).map((c) => ({
    id: c.id,
    title: c.title || c.id,
    pathwayKind: 'concentration'
  }));
  const subj = (courseSubjects || []).map((s) => ({
    id: s.id,
    title: s.title || s.id,
    pathwayKind: 'course_subject'
  }));
  const merged = [...cte, ...subj];
  merged.sort((a, b) => String(a.title).localeCompare(String(b.title), undefined, { sensitivity: 'base' }));
  return merged;
}
